import { useState, useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { billRefLabel, type BillRef } from '../../../../shared/contracts/bill-ref.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { PageHeader } from '../../../../shared/components/PageHeader.js';
import { Card } from '../../../../shared/components/Card.js';
import { Button } from '../../../../shared/components/Button.js';
import { Badge } from '../../../../shared/components/Badge.js';
import { Alert } from '../../../../shared/components/Alert.js';
import { EmptyState } from '../../../../shared/components/EmptyState.js';
import { personLabel } from '../../components/ApprovalLevels.js';

/** A `GET /v1/payments/eligibility/status/:status` row (T173 + bill / vendor references). */
interface EligibleClaim {
  readonly eligibilityId: string;
  /** The job, dealer and date the bill is known by. */
  readonly bill: BillRef | null;
  readonly vendorName: string | null;
  readonly vendorCode: string | null;
  readonly grossAmount: number;
  readonly paymentStatusCode: string;
}

const cell = { padding: '12px 14px', borderBottom: '1px solid var(--color-border-default)', verticalAlign: 'middle' } as const;
const head = { ...cell, fontSize: 12, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--color-text-muted)', textAlign: 'left', background: 'var(--color-bg-muted)' } as const;

/** L15.01 — Finance Maker Queue: bills cleared for payment, awaiting their payment voucher. */
export function MakerQueuePage(): ReactNode {
  const { t } = useTranslation();
  // Amounts follow the tenant's DISPLAY_NUMBER_FORMAT, not a locale in the source.
  const { money } = useBusinessNumber();
  const { day } = useBusinessDay();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState<EligibleClaim[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiRequest<EligibleClaim[]>('/v1/payments/eligibility/status/PAYMENT_ELIGIBLE', { token })
      .then(setItems)
      .catch(e => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <div className="tl-page" style={{ padding: 24 }}>
      <PageHeader title={t('finance.maker.title')} subtitle={t('finance.maker.subtitle')} />
      {error && <Alert tone="danger">{error}</Alert>}
      {loading && <p style={{ color: 'var(--color-text-muted)' }}>{t('finance.maker.loading')}</p>}
      {!loading && !error && items.length === 0 && <EmptyState title={t('finance.maker.empty')} />}
      {items.length > 0 && (
        <Card>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr>
                  <th style={head}>{t('finance.maker.colClaim')}</th>
                  <th style={head}>{t('finance.maker.colVendor')}</th>
                  <th style={{ ...head, textAlign: 'right' }}>{t('finance.maker.colAmount')}</th>
                  <th style={head}>{t('finance.maker.colStatus')}</th>
                  <th style={head}>{t('finance.maker.colAction')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.eligibilityId}>
                    <td style={cell}>{billRefLabel(item.bill, day)}</td>
                    <td style={cell}>{personLabel(item.vendorName, item.vendorCode) ?? '—'}</td>
                    <td style={{ ...cell, textAlign: 'right', fontWeight: 600 }}>{money(item.grossAmount)}</td>
                    <td style={cell}><Badge tone="info">{t(`finance.stage.${item.paymentStatusCode}`)}</Badge></td>
                    <td style={cell}>
                      <Button size="sm" variant="primary" onClick={() => navigate(`/finance/maker/vouchers/new?eligibilityId=${item.eligibilityId}`)}>{t('finance.maker.createVoucher')}</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
